/**
 * User-settable CSV dialect, shared by the comparison and the XLSX converter.
 *
 * Auto-detect is the default so neither tool asks anything of a user with an
 * ordinary comma-separated file. The overrides exist for the files that are not
 * ordinary — tab-separated exports, and files whose quote characters are literal
 * data rather than quoting.
 */

export interface CsvParseOptions {
  /** Let the parser work the dialect out. When true the fields below are unused. */
  autoDetect: boolean;
  /** Field separator, as typed — may be the escape `\t` rather than a raw tab. */
  delimiter: string;
  /** Quote character, as typed. Empty means the file has no quoting at all. */
  quoteChar: string;
}

export const DEFAULT_CSV_PARSE_OPTIONS: CsvParseOptions = {
  autoDetect: true,
  delimiter: ',',
  quoteChar: '"',
};

/**
 * Escapes accepted in the delimiter and quote fields.
 *
 * A tab cannot be typed into a text input — it moves focus — so `\t` has to be
 * writable. Once `\t` means something, `\\` must too, or a lone backslash
 * delimiter becomes unexpressible.
 */
const ESCAPES = new Map<string, string>([
  ['\\t', '\t'],
  ['\\\\', '\\'],
]);

/** Resolve what the user typed into the single character they meant. */
export function decodeCharInput(raw: string): string {
  return ESCAPES.get(raw) ?? raw;
}

/** Show a character the way it can be typed back in. */
export function encodeCharInput(value: string): string {
  for (const [escape, decoded] of ESCAPES) {
    if (value === decoded) return escape;
  }
  return value;
}

/**
 * Characters that cannot separate fields.
 *
 * Line endings would end the record instead, a quote would be ambiguous against
 * quoting itself, and a BOM is a file marker. Papa rejects exactly these
 * (`Papa.BAD_DELIMITERS`) and would silently fall back to guessing, so they are
 * refused up front where the user can see why.
 */
const FORBIDDEN_DELIMITERS = new Set(['\r', '\n', '"', '﻿']);

export type CsvParseOptionsField = 'delimiter' | 'quoteChar';

/**
 * Why the dialect cannot be used, keyed by field. Empty when it is usable — and
 * always empty while auto-detect is on, since nothing is read from those fields.
 */
export type CsvParseOptionsErrors = Partial<
  Record<CsvParseOptionsField, 'empty' | 'too-long' | 'forbidden'>
>;

export function validateCsvParseOptions(
  options: CsvParseOptions,
): CsvParseOptionsErrors {
  if (options.autoDetect) return {};

  const errors: CsvParseOptionsErrors = {};

  const delimiter = decodeCharInput(options.delimiter);
  if (delimiter.length === 0) errors.delimiter = 'empty';
  else if ([...delimiter].length > 1) errors.delimiter = 'too-long';
  else if (FORBIDDEN_DELIMITERS.has(delimiter)) errors.delimiter = 'forbidden';

  // Blank is meaningful here — it is how "this file has no quoting" is expressed
  // — so only an over-long value is wrong.
  const quoteChar = decodeCharInput(options.quoteChar);
  if ([...quoteChar].length > 1) errors.quoteChar = 'too-long';

  return errors;
}

/** Whether a dialect can be used to read a file as it stands. */
export function csvParseOptionsAreUsable(options: CsvParseOptions): boolean {
  return Object.keys(validateCsvParseOptions(options)).length === 0;
}

/** The dialect, resolved to the characters a parser needs. */
export interface ResolvedCsvDialect {
  /** Undefined asks the parser to detect it. */
  delimiter?: string;
  /** Undefined means the file is not quoted at all. */
  quoteChar?: string;
}

export function resolveCsvDialect(
  options: CsvParseOptions,
): ResolvedCsvDialect {
  if (options.autoDetect) return {};
  const quoteChar = decodeCharInput(options.quoteChar);
  return {
    delimiter: decodeCharInput(options.delimiter),
    quoteChar: quoteChar.length > 0 ? quoteChar : undefined,
  };
}

/**
 * Papa's config for a dialect.
 *
 * An absent `delimiter` is what makes Papa guess. Unquoted files go through
 * `fastMode` rather than an empty `quoteChar`: fast mode is Papa's documented
 * "no quoted fields" path, whereas an empty quote character leaves its scanner
 * matching at every position.
 */
export function toPapaConfig(options: CsvParseOptions): {
  delimiter?: string;
  quoteChar?: string;
  fastMode?: boolean;
} {
  const dialect = resolveCsvDialect(options);
  if (dialect.delimiter === undefined) return {};
  if (dialect.quoteChar === undefined) {
    return { delimiter: dialect.delimiter, fastMode: true };
  }
  return { delimiter: dialect.delimiter, quoteChar: dialect.quoteChar };
}

/**
 * The dialect as the converter contract's input shape, or undefined to let the
 * server detect the delimiter.
 */
export function toDialectInput(
  options: CsvParseOptions,
): { delimiter: string; quote: string } | undefined {
  const dialect = resolveCsvDialect(options);
  if (dialect.delimiter === undefined) return undefined;
  return {
    delimiter: dialect.delimiter,
    // Sent explicitly so the server can tell "no quoting" from "not specified".
    quote: dialect.quoteChar ?? '',
  };
}
